<template>
    <!-- Mobile overlay (below lg only) -->
    <div
      v-if="isOpen"
      data-testid="sidebar-overlay"
      class="fixed inset-0 z-30 bg-black/50 lg:hidden"
      @click="close"
    />

    <aside
      class="fixed inset-y-0 left-0 z-40 w-64 flex flex-col bg-primary-800 text-white transform transition-transform duration-300 lg:translate-x-0 lg:z-30"
      :class="isOpen ? 'translate-x-0' : '-translate-x-full'"
    >
      <div class="flex items-center justify-center h-16 bg-primary-900 shadow-md">
        <h1 class="text-xl font-display font-bold"><span class="text-oxo-red">OXO</span> Admin</h1>
      </div>

      <nav class="flex-1 px-4 py-6 space-y-4 overflow-y-auto">
        <NuxtLink to="/" class="nav-item mb-4" exact-active-class="active" @click="close">
          <font-awesome-icon :icon="['fas', 'chart-line']" class="w-5 h-5 mr-3" />
          Dashboard
        </NuxtLink>

        <!-- Dynamic Sections -->
        <div v-for="(section, index) in menu" :key="index" class="space-y-1">
          <button 
            @click="toggleSection(section.label)" 
            class="w-full flex items-center justify-between px-2 py-2 text-xs font-semibold text-primary-400 uppercase tracking-wider hover:text-white transition-colors focus:outline-hidden"
          >
            <span>{{ section.label }}</span>
            <font-awesome-icon :icon="['fas', openSections.includes(section.label) ? 'chevron-down' : 'chevron-right']" class="w-3 h-3 transition-transform" />
          </button>
          
          <div v-show="openSections.includes(section.label)" class="space-y-1 pl-2 transition-all duration-300 ease-in-out">
            <NuxtLink 
              v-for="item in section.items" 
              :key="item.path" 
              :to="item.path"
              class="nav-item"
              active-class="active"
              @click="close"
            >
              <font-awesome-icon :icon="item.icon" class="w-5 h-5 mr-3 opacity-70" />
              <span class="flex-1">{{ item.name }}</span>
              <span v-if="item.badge && item.badge > 0" class="ml-2 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-xs">
                {{ item.badge }}
              </span>
            </NuxtLink>
          </div>
        </div>

      </nav>

      <div class="p-4 bg-primary-900 border-t border-primary-700">
        <NuxtLink to="/profile" class="flex items-center hover:bg-primary-800 p-2 rounded-sm transition-colors group">
          <img class="h-8 w-8 rounded-full bg-primary-700 object-cover" :src="getAvatarUrl(currentUser?.avatar)" :alt="currentUser?.username" />
          <div class="ml-3">
            <p class="text-sm font-medium text-white group-hover:text-oxo-red transition-colors">{{ currentUser?.firstName }} {{ currentUser?.lastName }}</p>
            <p class="text-xs text-primary-400">{{ currentUser?.roles.join(', ') }}</p>
          </div>
        </NuxtLink>
        <button @click="logout" class="mt-4 w-full flex items-center justify-center px-4 py-2 text-sm text-primary-300 hover:text-white hover:bg-primary-800 rounded-sm transition-colors">
          <font-awesome-icon :icon="['fas', 'sign-out-alt']" class="mr-2" />
          Déconnexion
        </button>
      </div>
    </aside>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthService } from '~/composables/useAuthService'
import { useNotificationBadges } from '~/composables/useNotificationBadges'
import { useSidebar } from '~/composables/useSidebar'

const router = useRouter()
const route = useRoute()
const { isOpen, close } = useSidebar()
const { currentUser } = useAuthService()
const { badges, fetchBadges } = useNotificationBadges()
const config = useRuntimeConfig()
const openSections = ref<string[]>([]) // Closed by default

// Close the drawer on navigation (no effect on the lg+ fixed sidebar).
watch(() => route.path, () => close())

// Lock body scroll while the mobile drawer is open.
watch(isOpen, (open) => {
  if (typeof document !== 'undefined') {
    document.body.style.overflow = open ? 'hidden' : ''
  }
})

const onKey = (e: KeyboardEvent) => {
  if (e.key === 'Escape') close()
}
onMounted(() => document.addEventListener('keydown', onKey))
onUnmounted(() => {
  document.removeEventListener('keydown', onKey)
  if (typeof document !== 'undefined') document.body.style.overflow = ''
})

const getAvatarUrl = (path: string | undefined) => {
    if (!path) return '' 
    if (path.startsWith('http')) return path
    const cleanPath = path.startsWith('/') ? path : `/${path}`
    const host = config.public.apiHost || 'http://localhost:5000'
    return `${host}${cleanPath}`
}

const menu = computed(() => {
    const user = currentUser.value
    if (!user) return []

    // Helper: Checker permission
    const canAccess = (module: string) => {
        if (user.roles.includes('SuperAdmin') || user.roles.includes('Admin')) return true
        if (user.modules?.includes('all')) return true
        return user.modules?.includes(module)
    }

    const sections = []

    // 1. Administration
    const adminItems = []
    
        if (user.roles.includes('SuperAdmin') || user.roles.includes('Admin')) {
       adminItems.push({ name: 'Infos générales', path: '/school/general', icon: ['fas', 'school'] })
       adminItems.push({ name: 'Certifications', path: '/school/certifications', icon: ['fas', 'certificate'] })
    }

    if (canAccess('applications')) adminItems.push({ 
        name: 'Candidatures', 
        path: '/applications', 
        icon: ['fas', 'envelope-open-text'],
        badge: badges.value.applications 
    })
    adminItems.push({ 
        name: 'Messages', 
        path: '/messages', 
        icon: ['fas', 'envelope'],
        badge: badges.value.messages
    })
    
    if (canAccess('school_team') || canAccess('students') || canAccess('partners')) {
        adminItems.push({ name: 'Communauté', path: '/school/community', icon: ['fas', 'users-cog'] })
    }
    
    if (canAccess('formations')) adminItems.push({ name: 'Formations', path: '/formations', icon: ['fas', 'graduation-cap'] })
    if (user.roles.includes('SuperAdmin') || user.roles.includes('Admin')) {
        adminItems.push({ name: 'Ateliers d\'Été', path: '/workshops', icon: ['fas', 'sun'] })
        adminItems.push({ name: 'Pointage', path: '/attendance', icon: ['fas', 'id-card'] })
    }

    if (adminItems.length > 0) {
        sections.push({
            label: 'Administration',
            items: adminItems
        })
    }

    // 2. Ecole
    const schoolItems = []
    if (canAccess('news')) schoolItems.push({ name: 'Vie de l\'école', path: '/news', icon: ['fas', 'newspaper'] })
    if (canAccess('projects')) schoolItems.push({ name: 'Projets étudiants', path: '/projects', icon: ['fas', 'gamepad'] })
    
    if (schoolItems.length > 0) {
        sections.push({ label: 'Ecole', items: schoolItems })
    }

    // 3. CRM
    const crmItems = []
    if (user.roles.includes('SuperAdmin') || user.roles.includes('Admin') || canAccess('crm')) {
        crmItems.push({ name: 'Contacts', path: '/crm/contacts', icon: ['fas', 'users'] })
        crmItems.push({ name: 'Newsletter', path: '/crm/newsletter', icon: ['fas', 'paper-plane'] })
        crmItems.push({ name: 'Tags', path: '/crm/tags', icon: ['fas', 'tags'] })
        crmItems.push({ name: 'Modèles Newsletters', path: '/crm/newsletter/templates', icon: ['fas', 'newspaper'] })
        crmItems.push({ name: 'Modèles Emails Auto', path: '/crm/automated-templates', icon: ['fas', 'microchip'] })
    }
    
    if (crmItems.length > 0) {
        sections.push({ label: 'CRM', items: crmItems })
    }

    // 4. Site Web
    const websiteItems = []
    const canAccessContent = canAccess('presentation') || canAccess('home') || user.roles.includes('Editor') || user.roles.includes('SuperAdmin') || user.roles.includes('Admin')
    
    if (canAccessContent) {
        websiteItems.push({ name: 'Contenu Site', path: '/website/content', icon: ['fas', 'layer-group'] })
        websiteItems.push({ name: 'Hero Header', path: '/website/heroes', icon: ['fas', 'image'] })
        websiteItems.push({ name: 'Vidéos', path: '/school/videos', icon: ['fas', 'video'] })
        websiteItems.push({ name: 'Médiathèque', path: '/website/media', icon: ['fas', 'images'] })
    }

    if (canAccess('reviews')) websiteItems.push({ name: 'Qualiopi', path: '/qualiopi', icon: ['fas', 'certificate'] })
    if (canAccessContent) websiteItems.push({ name: 'Témoignages', path: '/testimonials', icon: ['fas', 'quote-right'] })
    if (canAccess('faq')) websiteItems.push({ name: 'FAQ', path: '/faq', icon: ['fas', 'question-circle'] })
    
    // SEO is accessible to admins and editors
    if (canAccessContent) websiteItems.push({ name: 'SEO Global', path: '/school/seo', icon: ['fas', 'search'] })

    if (websiteItems.length > 0) {
        sections.push({ label: 'Site Web', items: websiteItems })
    }

    // 5. Système
    if (user.roles.includes('SuperAdmin') || user.roles.includes('Admin')) {
        sections.push({
            label: 'Système',
            items: [
              { name: 'Paramètres OXO', path: '/settings', icon: ['fas', 'cogs'] },
              { name: 'Utilisateurs', path: '/settings/users', icon: ['fas', 'users-cog'] },
              { name: 'Activités logs', path: '/activity-logs', icon: ['fas', 'clipboard-list'] }
            ]
        })
    }

    return sections
})

const toggleSection = (label: string) => {
  if (openSections.value.includes(label)) {
    openSections.value = openSections.value.filter(s => s !== label)
  } else {
    openSections.value.push(label)
  }
}

const logout = () => {
    const token = useCookie('auth_token')
    token.value = null 
    router.push('/auth/login')
}

onMounted(() => {
    fetchBadges()
})
</script>

<style scoped>
@reference "../../assets/css/main.css";

.nav-item {
  @apply flex items-center px-4 py-2.5 text-sm font-medium text-primary-300 rounded-md transition-colors hover:bg-primary-700 hover:text-white;
}
.nav-item.active {
  @apply bg-primary-700 text-white border-l-4 border-oxo-red pl-3;
}
</style>
